import { templateCache } from "@/features/signature/services/template-cache";
import { API_BASE_URL, API_TOKEN } from "@/shared/constants/env";
import { profilePermissions } from "@/shared/services/profile-permissions";
import type { SignatureTemplate } from "@/shared/types";

/** Timeout da requisição HTTP para evitar bloqueio prolongado da UI (ms). */
const FETCH_TIMEOUT_MS = 5_000;

/**
 * Busca os templates de assinatura da API.
 *
 * Inclui timeout de {@link FETCH_TIMEOUT_MS} via `AbortController` para evitar
 * que a interface fique bloqueada quando o backend está indisponível.
 *
 * @param userEmail - E-mail do usuário para filtro no backend.
 * @param userName - Nome do usuário, usado pelo backend para auto-provisionar o
 * recipient na primeira chamada (não sobrescreve nome já cadastrado).
 * @param shareToRoaming - `false` no runtime de background, que não deve
 * escrever no `roamingSettings` (ver {@link templateCache.set}).
 * @returns Lista de templates retornada pela API.
 * @throws Se a requisição falhar, expirar ou a API responder com erro.
 *
 * @example
 * const templates = await fetchSignatureTemplates("user@empresa.com", "Fulano de Tal");
 */
export async function fetchSignatureTemplates(
  userEmail: string,
  userName?: string,
  shareToRoaming: boolean = true,
): Promise<SignatureTemplate[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${API_BASE_URL}/signatures/templates`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${API_TOKEN}`,
        "X-User-Email": userEmail,
        ...(userName ? { "X-User-Name": userName } : {}),
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Erro API: ${response.status} - ${response.statusText}`);
    }

    const data: SignatureTemplate[] = await response.json();
    templateCache.set(data, shareToRoaming);
    profilePermissions.syncFromTemplates(data);
    return data;
  } catch (error: any) {
    handleMixedContentWarning();

    if (error?.name === "AbortError") {
      throw new Error(`Timeout ao buscar templates da API após ${FETCH_TIMEOUT_MS}ms.`);
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Rebusca os templates apenas se o cache já passou da janela de
 * `TEMPLATES_REFRESH_MS` (10 minutos).
 *
 * Nunca lança: é chamada em fluxos oportunistas (timer do painel e runtime de
 * background) onde uma falha de rede não deve interromper nada — o cache atual
 * continua valendo.
 *
 * @param userEmail - E-mail do usuário para filtro no backend.
 * @param userName - Nome do usuário, para auto-provisionar o recipient.
 * @param shareToRoaming - `false` no runtime de background.
 * @returns `true` se buscou e atualizou o cache.
 *
 * @example
 * await refreshTemplatesIfStale("user@empresa.com", "Fulano de Tal");
 */
export async function refreshTemplatesIfStale(
  userEmail: string,
  userName?: string,
  shareToRoaming: boolean = true,
): Promise<boolean> {
  if (!userEmail || !templateCache.isStale()) return false;

  try {
    await fetchSignatureTemplates(userEmail, userName, shareToRoaming);
    return true;
  } catch (error: any) {
    console.warn("[API] Refresh de templates falhou — mantendo cache:", error?.message || error);
    return false;
  }
}

function handleMixedContentWarning(): void {
  if (typeof window !== "undefined" && window.location.protocol === "https:" && API_BASE_URL.startsWith("http:")) {
    console.warn(
      "[API] AVISO: Mixed Content — API HTTP acessada de origem HTTPS. " +
      "Use HTTPS no backend ou ngrok para testes."
    );
  }
}
